import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import Command from '@ckeditor/ckeditor5-core/src/command';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import PresenceList from '@ckeditor/ckeditor5-real-time-collaboration/src/presencelist';
import RealTimeCollaborativeEditing from '@ckeditor/ckeditor5-real-time-collaboration/src/realtimecollaborativeediting';
import ContextWatchdog from '@ckeditor/ckeditor5-watchdog/src/contextwatchdog';
import { PDFExport } from '@progress/kendo-react-pdf';
import classNames from 'classnames';
import throttle from 'lodash.throttle';
import isEmpty from 'lodash/isEmpty';
import LogRocket from 'logrocket';
import Animate from 'rc-animate';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { DocumentsAPI } from 'api/document/document-core';
import { Document } from 'api/document/types/document';
import { DocumentId, ProjectId } from 'api/models';
import { EDITOR_LOCK_KEY } from 'constant';
import {
  ContentTaskIdMap,
  ResourceSectionType,
  setActiveDocument,
  setDocumentResources,
  setTasksDocMap,
  setTaskSectionNameIdMap,
  updateTasksDocMapWithKeys,
} from 'data/documents';
import { logger } from 'services';
import { typecastError } from 'utils/error';
import { DocumentFreezeCommand } from 'vendor/ckeditor/plugins/freeze-document';
import {
  CommentService,
  ContentCommentIdMap,
} from '../document/services/comment';
import { Context } from './context-editor';
import { CustomEditor } from './custom-editor';
import { CkeProps, State } from './custom-editor.types';
import { EditorModes } from './editor-enum';
import { getDocumentName } from './get-document-name';
import { CKContextConfig, getCkEditorConfig, handleCKErrors } from './helpers';
import { DocLoader } from './parts';
import styles from './styles.module.css';

const toggleReadOnlyMode = (isReadOnly, editor) => {
  if (!editor) {
    return;
  }
  if (isReadOnly) {
    editor.enableReadOnlyMode(EDITOR_LOCK_KEY);
  } else {
    editor.disableReadOnlyMode(EDITOR_LOCK_KEY);
  }
};

// How much time we can wait to kickstart autosave
const AUTO_SAVE_INIT_DELAY = 6000;
const WAIT_TILL_LOAD = 250;
const moveCaretToEnd = (editor: Editor, documentTabsFreeMode: boolean) => {
  editor.model.change((writer) => {
    if (documentTabsFreeMode) {
      editor.editing.view.focus();
    }
    writer.setSelection(editor.model.document.getRoot(), 'end');
  });
};

interface UndoRedoOptions {
  forced?: boolean;
}
class CKCollabEditorBase extends React.PureComponent<CkeProps, State> {
  editorRef = React.createRef<HTMLDivElement>();

  // Currently we're deprioritising multiple private sections and
  // going with one private section on top. Once we know how
  // much of private section needed after v1, we will revisit this implementation.
  privateEditorRef = React.createRef<HTMLDivElement>();

  editors: Editor[] = [];

  undoStack: number[] = [];

  redoStack: number[] = [];

  batches = new WeakSet();

  contextChannelId: string;

  constructor(props) {
    super(props);
    // TODO: Show private section only if there's some content.
    this.state = {
      publicEditor: null,
      privateEditor: null,
      publicContent: '',
      publicSectionId: 0,
      privateContent: '',
      privateSectionId: 0,
      isEditorReady: false,
      loadingMessage: 'document.state.retrieving',
      hideLoading: false,
      placeholder: {
        [EditorModes.Public]: true,
        [EditorModes.Private]: true,
      },
    };
    this.contextChannelId = `rl-doc-${props.docId}-v28`;

    this.getDocument = this.getDocument.bind(this);
    this.onError = this.onError.bind(this);
  }

  async componentDidMount() {
    try {
      // Get document
      const documentContent = await this.getDocument();

      const { singleUserMode } = this.props;

      if (!singleUserMode) {
        this.setState({
          loadingMessage: 'document.state.waiting_for_ck',
        });
      }
      const { dispatch, onDocumentTitleChange, onDocumentLoaded } = this.props;

      if (onDocumentLoaded) {
        onDocumentLoaded(documentContent.document as Document);
      }

      this.setState(
        {
          publicContent: documentContent.public?.content || '',
          publicSectionId: documentContent.public?.documentSectionId || 0,
          privateContent: documentContent.private?.content || '',
          privateSectionId: documentContent.private?.documentSectionId || 0,
          placeholder: {
            [EditorModes.Public]: !documentContent.public?.content,
            [EditorModes.Private]: !documentContent.private?.content,
          },
        },
        async () => {
          try {
            if (documentContent.public?.documentSectionId) {
              await this.createDocumentContext();
              dispatch(
                setActiveDocument({
                  publicSection: {
                    id: documentContent.public?.documentSectionId,
                    type: EditorModes.Public,
                  },
                  privateSection: {
                    id: documentContent.private?.documentSectionId,
                    type: EditorModes.Private,
                  },
                }),
              );
              if (onDocumentTitleChange) {
                onDocumentTitleChange(documentContent.title);
              }
              // We need to create the private section upfront for autosave to
              // work correctly as CK autosave gets only editor as the param
              // https://ckeditor.com/docs/ckeditor5/latest/builds/guides/integration/saving-data.html#autosave-feature
              const { docId, projectId } = this.props;

              if (!documentContent.private?.documentSectionId && projectId) {
                const content = await DocumentsAPI.createPrivateSection(
                  projectId,
                  docId,
                  '',
                );

                const pvtSectionId = content.sections.find(
                  (section) => section.meta.sectionType === EditorModes.Private,
                )?.documentSectionId;

                if (pvtSectionId) {
                  this.setState({
                    privateSectionId: pvtSectionId,
                  });
                }
              }
              if (documentContent.frozen) {
                this.editors.forEach((editor) =>
                  editor.execute(DocumentFreezeCommand.Freeze),
                );
              }
            } else {
              logger.warn(
                'Document section data not found for, ',
                `${documentContent.document.documentId} : ${documentContent.document.title}`,
              );
            }
          } catch (error) {
            const { docId, projectId } = this.props;
            const err = typecastError(error, `CKE Document error for ${docId}`);

            logger.error(`cke: Document error for ${docId}`, {
              error,
            });
            LogRocket.captureException(err, {
              tags: {
                // additional data to be grouped as "tags"
                projectId: projectId || 'unknown',
                docId,
              },
              extra: {
                pageUrl: window.location.href,
              },
            });
          }
        },
      );
    } catch (error) {
      const { docId, projectId } = this.props;
      const err = typecastError(error, `CKE Document error for ${docId}`);

      logger.error(`cke: Document error for ${docId}`, {
        error,
      });
      LogRocket.captureException(err, {
        tags: {
          // additional data to be grouped as "tags"
          projectId: projectId || 'unknown',
          docId,
        },
        extra: {
          pageUrl: window.location.href,
        },
      });
    }
    // TODO: @asimkt We may not need this handler. Remove after verifying that CKE errors are not coming here.
    // Why? This handler was added before the refresh mechanism, and errors were coming here.
    // now it's best to keep the refresh mechanism stable
    // instead of handling CKE session in different places.
    window.addEventListener('error', handleCKErrors);
  }

  componentWillUnmount() {
    const { publicEditor, privateEditor } = this.state;
    const { presenceListElementRef } = this.props;

    publicEditor?.destroy();
    privateEditor?.destroy();
    if (presenceListElementRef.current) {
      presenceListElementRef.current.innerHTML = '';
    }
    window.removeEventListener('error', handleCKErrors);
  }

  onError(error: Error) {
    const { onEditorError } = this.props;

    if (onEditorError) {
      onEditorError(error);
    }
  }

  async getDocument() {
    const { dispatch } = this.props;
    const { docId, projectId, apiService } = this.props;
    const documentGetApi = async (): Promise<{
      document: AtLeast<Document, 'documentId' | 'sections' | 'title'>;
    }> => {
      if (projectId === undefined) {
        throw new Error('projectId is not present');
      }

      const data = await DocumentsAPI.getDocument(projectId, docId);

      return Promise.resolve(data);
    };
    const getApi = apiService?.getDocument || documentGetApi;
    const apiResponse = await getApi();

    const privateSection = apiResponse.document.sections.find(
      (section) => section.meta.sectionType === EditorModes.Private,
    );
    const publicSection = apiResponse.document.sections.find(
      (section) => section.meta.sectionType === EditorModes.Public,
    );

    const privateTaskSectionNameMap =
      privateSection?.meta.resourceSections || [];
    const publicTaskSectionNameMap = publicSection?.meta.resourceSections || [];

    let resourceSectionNameMap = privateTaskSectionNameMap.reduce(
      (acc, mapObj) => {
        if (
          mapObj.resourceSectionId &&
          mapObj?.resourceSectionType === ResourceSectionType.DocumentTask
        ) {
          acc[mapObj.resourceSectionId] = mapObj.resourceSectionName;
        }

        return acc;
      },
      {},
    );

    resourceSectionNameMap = publicTaskSectionNameMap.reduce((acc, mapObj) => {
      if (
        mapObj.resourceSectionId &&
        mapObj?.resourceSectionType === ResourceSectionType.DocumentTask // we should put only the doc sections in the idMap, not the status update resource sections
      ) {
        acc[mapObj.resourceSectionId] = {
          content: mapObj.resourceSectionName,
          parentDoc: docId,
          resourceSectionType: ResourceSectionType.DocumentTask,
          resourceObjects: mapObj?.resourceObjects ?? [],
        };
      }

      return acc;
    }, resourceSectionNameMap);

    if (!isEmpty(resourceSectionNameMap)) {
      dispatch(setTaskSectionNameIdMap(resourceSectionNameMap));
    }
    dispatch(
      setDocumentResources([
        ...privateTaskSectionNameMap,
        ...publicTaskSectionNameMap,
      ]),
    );

    return {
      private: privateSection,
      public: publicSection,
      title: apiResponse.document.title,
      frozen: apiResponse.document.frozen,
      document: apiResponse.document,
    };
  }

  handleUndoRedo = () => {
    const { readOnly } = this.props;

    if (readOnly) {
      return;
    }
    const { editors } = this;

    // Nothing special for single editor
    if (editors.length < 2) {
      return;
    }
    editors.forEach((editor, i) => {
      const undo = editor.commands?.get<Command>('undo');
      const redo = editor.commands?.get<Command>('redo');
      const editing = editor.plugins?.get<any>('UndoEditing');

      editor.model.on<any[]>('applyOperation', (_event, args) => {
        const operation = args[0];

        if (!operation.isDocumentOperation) {
          return;
        }
        const { batch } = operation;
        // eslint-disable-next-line no-underscore-dangle
        const registeredBatch = editing._batchRegistry.has(batch);

        if (
          !registeredBatch ||
          batch.type === 'transparent' ||
          this.batches.has(batch)
        ) {
          return;
        }
        this.batches.add(batch);
        this.undoStack.push(i);
        this.redoStack = [];
      });

      undo.on<UndoRedoOptions[]>(
        'execute',
        (event, args) => {
          const forced = !!args[0]?.forced;

          if (forced) {
            return;
          }
          event.stop();
          const index = this.undoStack.pop() || 0;
          const nextEditor = this.editors[index];

          nextEditor.editing.view.focus();
          nextEditor.execute('undo', { forced: true });
          this.redoStack.push(index);
        },
        { priority: 'highest' },
      );

      redo.on<UndoRedoOptions[]>(
        'execute',
        (event, args) => {
          const forced = !!args[0]?.forced;

          if (forced) {
            return;
          }
          event.stop();
          const index = this.redoStack.pop() || 0;
          const nextEditor = this.editors[index];

          nextEditor.editing.view.focus();
          nextEditor.execute('redo', { forced: true });
          this.undoStack.push(index);
        },
        { priority: 'highest' },
      );
    });
  };

  async createDocumentContext() {
    const {
      singleUserMode,
      presenceListElementRef,
      extraContextConfig = {},
    } = this.props;

    const contextConfig = singleUserMode
      ? {
          cloudServices: {
            ...CKContextConfig.cloudServices,
            tokenUrl: () => DocumentsAPI.getCollaborationToken(),
          },
        }
      : Object.assign(
          CKContextConfig,
          {
            plugins: [],
            collaboration: {
              channelId: this.contextChannelId,
            },
            presenceList: {
              container: presenceListElementRef.current,
            },
            cloudServices: {
              ...CKContextConfig.cloudServices,
              tokenUrl: () => DocumentsAPI.getCollaborationToken(),
            },
          },
          extraContextConfig,
        );

    const watchdog = new ContextWatchdog(Context, {
      crashNumberLimit: 10,
    });

    // Initialize the watchdog with the context configuration:
    await watchdog.create(contextConfig);

    // The `error` event is fired when the context watchdog catches a context-related error.
    // Note that errors fired by items are not delegated to `ContextWatchdog#event:error`.
    // See also `ContextWatchdog#event:itemError`.
    watchdog.on('error', (args, { error }) => {
      this.setState({
        loadingMessage: 'document.state.hold_on',
        hideLoading: false,
      });

      logger.warn('Editor: Context have encountered an error ', {
        error,
        args,
      });

      this.onError(error);
    });
    // The `restart` event is fired when the context is set back to the `ready` state (after it was in the `crashed` state).
    // Similarly, this event is not thrown for internal item restarts.
    watchdog.on('restart', () => {
      this.setState({
        loadingMessage: 'document.state.loaded',
        hideLoading: true,
      });
      logger.info('Editor: The context has been restarted.');
    });

    // The `itemError` event is fired when an error occurred in one of the added items.
    watchdog.on('itemError', (_, { error, itemId }) => {
      this.setState({
        loadingMessage: 'document.state.hold_on',
        hideLoading: false,
      });
      logger.warn(
        `Editor:  An error occurred in an editor with the '${itemId}' editor ID.`,
        { error },
      );
      this.onError(error);
    });

    // The `itemRestart` event is fired when an item is set back to the `ready` state (after it was in the `crashed` state).
    watchdog.on('itemRestart', (_, { itemId }) => {
      logger.info(
        `Editor:  An item with with the '${itemId}' ID has been restarted.`,
      );
      this.setState({
        loadingMessage: 'document.state.loaded',
        hideLoading: true,
      });
    });
    // Context.create(contextConfig).then((context) => {
    const {
      emitPrivateSectionStatus,
      isPrivateSectionEnabled,
      onEditorFocusChange,
    } = this.props;

    const editorParams = {
      [EditorModes.Public]: {
        editorRef: this.editorRef.current,
        editorMode: EditorModes.Public,
        watchdog,
      },
      [EditorModes.Private]: {
        editorRef: this.privateEditorRef.current,
        editorMode: EditorModes.Private,
        watchdog,
      },
    };
    const editorModes = isPrivateSectionEnabled
      ? [EditorModes.Public, EditorModes.Private]
      : [EditorModes.Public];

    const [publicEditor, privateEditor] = await Promise.all(
      editorModes.map((mode) =>
        this.generateEditor(
          editorParams[mode].editorMode,
          editorParams[mode].editorRef,
          editorParams[mode].watchdog,
        ),
      ),
    );

    const { readOnly, documentTabsFreeMode } = this.props;

    if (typeof publicEditor === 'object') {
      toggleReadOnlyMode(readOnly, publicEditor);
      window.setTimeout(() => {
        moveCaretToEnd(publicEditor, documentTabsFreeMode!);
      }, WAIT_TILL_LOAD); // since editor instance isn't constructed yet and loading would be hidden after 500ms we are also following the same.
    }
    /**
     * TODO: Use a list of editors as they appear on screen instead of
     * this manual ordering. We use push instead of setting value of an
     * index as this operation is asynchronous and can happen later than the
     * private editor. A better way is to order the editors and promises or
     * assign to predefined indexes using closures.
     */
    this.editors.push(publicEditor);
    this.handleUndoRedo();
    if (isPrivateSectionEnabled) {
      const { privateContent } = this.state;

      // Hide the private section initially if there's no content.
      if (!privateContent.length && emitPrivateSectionStatus) {
        emitPrivateSectionStatus(false);
      } else if (onEditorFocusChange) {
        onEditorFocusChange(privateEditor);
      }
      // If user deletes whole private section, and do one more backspace,
      // remove the private section altogether.
      privateEditor.editing?.view.document.on(
        'delete',
        () => {
          if (
            privateEditor.getData().length === 0 &&
            emitPrivateSectionStatus
          ) {
            emitPrivateSectionStatus(false);
          }
        },
        { priority: 'highest' },
      );
      /**
       * TODO: Use a list of editors as they appear on screen instead of
       * this manual ordering. We know private editor is the first so we
       * prepend to the list of editors to maintain position. Keep in mind
       * that the promise resolution is asynchronous so we need to spread
       * rest of the array.
       */
      this.editors.unshift(privateEditor);
      this.handleUndoRedo();
      this.setState({
        loadingMessage: 'document.state.loaded',
      });
      window.setTimeout(() => {
        this.setState({
          hideLoading: true,
        });
      }, WAIT_TILL_LOAD);
    } else {
      this.setState({
        loadingMessage: 'document.state.loaded',
      });
      window.setTimeout(() => {
        this.setState({
          hideLoading: true,
        });
      }, WAIT_TILL_LOAD);
    }
  }

  changePlaceholder(id: EditorModes, data: string) {
    const { placeholder } = this.state;

    if (!data) {
      this.setState({
        placeholder: {
          ...placeholder,
          [id]: true,
        },
      });
    } else {
      this.setState({
        placeholder: {
          ...placeholder,
          [id]: false,
        },
      });
    }
  }

  async generateEditor(
    id: EditorModes,
    el: HTMLDivElement | null,
    watchdog: any,
  ): Promise<Editor> {
    const {
      docId,
      projectId,
      isPrivateSectionEnabled,
      singleUserMode,
      onEditorReady,
      onInitialisationError,
      onEditorFocusChange,
      dispatch,
    } = this.props;
    const { extraPlugins, extraConfig } = this.props;
    const { publicContent, privateContent } = this.state;
    const editorId = getDocumentName(DocumentId(docId), id);
    const ckConfig = getCkEditorConfig();

    let updatedConfig = {
      ...ckConfig,
      initialData: id === EditorModes.Public ? publicContent : privateContent,
      collaboration: {
        channelId: editorId,
      },
    };

    if (isPrivateSectionEnabled) {
      updatedConfig = Object.assign(updatedConfig, {
        typing: { undoStep: 1 },
      });
    }

    const updatedPluginsArray = singleUserMode
      ? updatedConfig.plugins
      : [...updatedConfig.plugins, PresenceList, RealTimeCollaborativeEditing];

    // Update config with passed plugins and config
    updatedConfig = extraPlugins?.length
      ? Object.assign(updatedConfig, {
          plugins: [...updatedPluginsArray, ...extraPlugins],
        })
      : Object.assign(updatedConfig, {
          plugins: updatedPluginsArray,
        });

    updatedConfig = Object.assign(updatedConfig, extraConfig);

    try {
      await watchdog.add({
        id: editorId,
        type: 'editor',
        sourceElementOrData: el,
        config: updatedConfig,
        creator: (element, config) => {
          if (projectId && docId) {
            return CustomEditor.create(element, config, {
              docId: DocumentId(docId),
              projectId: ProjectId(projectId),
              onInitialisationError,
            });
          }

          return CustomEditor.create(element, config);
        },
      });
      const editor = watchdog.getItem(editorId);
      const stateObj =
        id === EditorModes.Public
          ? { publicEditor: editor }
          : { privateEditor: editor };

      this.setState(
        Object.assign(stateObj, {
          isEditorReady: true,
        }),
      );
      const editorRef =
        id === EditorModes.Public
          ? this.editorRef.current
          : this.privateEditorRef.current;

      if (onEditorFocusChange) {
        // Set focus to one initially.
        if (id === EditorModes.Public) {
          onEditorFocusChange(editor);
        }
        editor.editing.view.document.on('focus', () => {
          onEditorFocusChange(editor);
        });
      }
      const getAvailableCommentKeys = (ckEditorEl): string[] => {
        const comments = ckEditorEl?.querySelectorAll('[data-commentid]') || [];

        return Array.from(comments as Element[]).reduce(
          (acc: string[], elt: Element) => {
            const elId = elt.getAttribute('data-commentid') || '';
            const threadId = elId.split('-')[2];

            acc.push(threadId);

            return acc;
          },
          [],
        );
      };

      const getAvailableTaskKeys = (ckEditorEl): string[] => {
        const tasks = ckEditorEl?.querySelectorAll('[data-taskid]') || [];

        return Array.from(tasks as Element[]).reduce(
          (acc: string[], elt: Element) => {
            const elId = elt.getAttribute('data-taskid') || '';
            const threadId = elId.split('-')[2];

            acc.push(threadId);

            return acc;
          },
          [],
        );
      };

      const getAvailableContentCommentMap = (ckEditorEl) => {
        const comments = ckEditorEl?.querySelectorAll('[data-commentid]') || [];

        return Array.from(comments as Element[]).reduce(
          (acc: ContentCommentIdMap, elt: Element) => {
            const elId = elt.getAttribute('data-commentid') || '';
            const threadId = elId.split('-')[2];

            acc[threadId] = {
              content: elt.textContent,
              editorType: id,
              isActive: true,
              docId,
            };

            return acc;
          },
          {},
        );
      };

      const getAvailableContentTaskMap = (ckEditorEl) => {
        const tasks = ckEditorEl?.querySelectorAll('[data-taskid]') || [];

        return Array.from(tasks as Element[]).reduce(
          (acc: ContentTaskIdMap, elt: Element) => {
            const elId = elt.getAttribute('data-taskid') || '';
            const threadId = elId.split('-')[2];

            acc[threadId] = {
              content: elt.textContent,
              isActive: true,
              parentDocId: docId,
            };

            return acc;
          },
          {},
        );
      };

      const handleDataChange = (e, batch) => {
        const operation = batch.operations.find((op) => op.isDocumentOperation);

        if (operation) {
          window.setTimeout(() => {
            // Atleast one document operation happened. Update our content comment map.
            const ckEditorEl =
              editorRef?.parentElement?.querySelector('.ck-content');
            const commentsMap = getAvailableCommentKeys(ckEditorEl);
            const tasksMap = getAvailableTaskKeys(ckEditorEl);

            CommentService.updateContentIdMapWithKeys(commentsMap, docId, id);
            dispatch(updateTasksDocMapWithKeys(tasksMap));
            // if operation is from collaboration, update the full map content
            if (batch.type === 'transparent') {
              const contentCommentMap =
                getAvailableContentCommentMap(ckEditorEl);
              const contentTaskMap = getAvailableContentTaskMap(ckEditorEl);

              CommentService.updateContentIdMap(contentCommentMap);
              dispatch(setTasksDocMap(contentTaskMap));
            }
          }, 100);
        }
        this.changePlaceholder(id, editor.getData());
      };

      editor.model.document.on(
        'change:data',
        throttle(handleDataChange, 1000),
        {
          priority: 'lowest',
        },
      );
      window.setTimeout(() => {
        const { publicEditor, privateEditor } = this.state;
        const ckEditorEl =
          editorRef?.parentElement?.querySelector('.ck-content');
        const contentCommentMap = getAvailableContentCommentMap(ckEditorEl);
        const contentTaskMap = getAvailableContentTaskMap(ckEditorEl);

        CommentService.updateContentIdMap(contentCommentMap);

        dispatch(setTasksDocMap(contentTaskMap));
        if (onEditorReady) {
          onEditorReady({
            [EditorModes.Public]: publicEditor,
            [EditorModes.Private]: privateEditor,
          });
        }
      }, 100);

      this.changePlaceholder(id, editor.getData());

      window.setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        editor.readyToAutoSave = true;
      }, AUTO_SAVE_INIT_DELAY);

      return Promise.resolve(editor);
    } catch (error: any) {
      const err = typecastError(error, `ck failed to generate editor ${docId}`);

      logger.error('ck failed to generate editor', { error });
      logger.error(` ck failed to generate editor ${error?.message}`, {
        error,
      });
      logger.error(`ck failed to generate editor ${error?.stack}`, { error });
      logger.error(`ck failed to generate editor ${error?.cause}`, { error });
      const { onErrorRetry } = this.props;

      onErrorRetry?.(error);

      return Promise.reject(err.stack);
    }
  }

  render() {
    const {
      isEditorReady,
      privateEditor,
      publicContent,
      publicSectionId,
      privateContent,
      privateSectionId,
      placeholder,
      loadingMessage,
      hideLoading,
    } = this.state;

    const {
      isPrivateSectionEnabled,
      docId,
      projectId,
      customClasses,
      readOnly,
      maxWidth = false,
      className = '',
      pdfExportComponent,
      title,
      isResponsive,
      shouldHidePlaceHolder = false,
    } = this.props;

    return (
      <div
        className={classNames(styles.container, {
          [className]: true,
          [styles.container_full_responsive]: isResponsive,
          [styles.container_full]: !isResponsive && maxWidth,
        })}
      >
        <Animate transitionName="fade" animation>
          {!hideLoading ? <DocLoader loadingMessage={loadingMessage} /> : null}
        </Animate>
        <div
          className={`${styles.loader} ${isEditorReady ? styles.ready : ''}
          ${readOnly ? styles.readOnly : ''}
          ${customClasses?.editorClass ? customClasses?.editorClass : ''}`}
        >
          <div
            className={`${styles.private}`}
            // eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: privateContent }}
          />
          <div
            // eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: publicContent }}
          />
        </div>
        <div className={`${readOnly ? styles.readOnly : ''}`}>
          <div
            className={`${styles.private__container}
            ${isEditorReady ? styles.ready : ''}
            editor_section__private
            `}
          >
            <div
              className={`${styles.private} ${
                isPrivateSectionEnabled && privateEditor ? 'd-block' : ''
              }`}
            >
              {placeholder[EditorModes.Private] && !shouldHidePlaceHolder ? (
                <p
                  className={`${styles.placeholder} ${styles.placeholder__private}`}
                >
                  <FormattedMessage id="editor.placeholder" />
                </p>
              ) : null}
              <ViewOff16 className={styles.private__icon} />
              <div
                data-sectionid={privateSectionId}
                data-isprivate
                data-projectid={projectId}
                data-docid={docId}
                ref={this.privateEditorRef}
                id={EditorModes.Private}
              />
            </div>
          </div>
          <PDFExport
            forcePageBreak=".page-break"
            ref={pdfExportComponent}
            paperSize="A3"
            margin={{
              top: '2.54cm',
              bottom: '2.54cm',
            }}
            fileName={title ?? 'DocTitle'}
            title={title ?? 'DocTitle'}
          >
            <div
              className={`${styles.editor} ${
                isEditorReady ? styles.ready : ''
              } ${customClasses?.editorClass ? customClasses?.editorClass : ''}
            editor_section__public`}
            >
              {placeholder[EditorModes.Public] && !shouldHidePlaceHolder ? (
                <p className={styles.placeholder}>
                  <FormattedMessage id="editor.placeholder" />
                </p>
              ) : null}
              <div
                data-sectionid={publicSectionId}
                data-projectid={projectId}
                data-docid={docId}
                id={EditorModes.Public}
                ref={this.editorRef}
              />
            </div>
          </PDFExport>
        </div>
      </div>
    );
  }
}

export const ConnectedCKCollabEditor = connect()(CKCollabEditorBase);
